import { computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap, tap } from 'rxjs';
import { UserService } from 'shared/domain';
import { tapAllResponses } from 'shared/util-common';

import { Taxonomy, TaxonomyNode } from '@treetop/shared/model';

import { TaxonomyHttpService } from './taxonomy.http.service';

type TaxonomyTreeState = {
  taxonomy?: TaxonomyNode[];
  loading: boolean;
  error?: Error;
  selected: TaxonomyNode[];
  disabled: TaxonomyNode[];
  requestedOrganisationId?: string;
};

const initialState: TaxonomyTreeState = {
  taxonomy: null,
  loading: false,
  selected: [],
  disabled: [],
  requestedOrganisationId: undefined,
};

export const TaxonomyTreeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    vm: computed(() => ({
      taxonomy: store.taxonomy(),
      flatTaxonomy: flattenTaxonomy(store.taxonomy()),
      selected: store.selected(),
      disabled: store.disabled(),
      loading: store.loading(),
    })),
  })),
  withMethods((store) => {
    const taxonomyHttpService = inject(TaxonomyHttpService);
    const userService = inject(UserService);

    const setSelected = (selected: TaxonomyNode[]) => {
      patchState(store, { selected });
    };

    const setDisableAllDescendants = (nodes: TaxonomyNode[]) => {
      const flatTaxonomy = flattenTaxonomy(store.taxonomy());
      const descendants: TaxonomyNode[] = [];

      for (const node of nodes) {
        const taxonomyNode = flatTaxonomy[node.id];
        if (taxonomyNode?.children) {
          collectAllChildren(taxonomyNode.children, descendants);
        }
      }

      patchState(store, { disabled: descendants });
    };

    const loadTaxonomy = rxMethod<{ silent?: boolean; organisationId?: string }>((trigger$) =>
      trigger$.pipe(
        tap((trigger) => {
          patchState(store, { loading: !trigger.silent });
          patchState(store, { requestedOrganisationId: trigger.organisationId });
        }),
        switchMap((trigger) =>
          userService
            .getTableOfContent()
            .pipe(
              switchMap(() =>
                taxonomyHttpService.getTaxonomy(
                  trigger.organisationId ? { filter: { organisationId: trigger.organisationId } } : undefined,
                ),
              ),
            ),
        ),
        tapAllResponses<TaxonomyNode[], Error>(
          (taxonomy) => patchState(store, { loading: false, taxonomy: [...taxonomy] }),
          (error) => {
            patchState(store, { loading: false, error });
          },
        ),
      ),
    );

    const addNode = rxMethod<TaxonomyNode>((node$) =>
      node$.pipe(
        switchMap((node) => taxonomyHttpService.addNode(node)),
        tapAllResponses<Taxonomy[], Error>(
          () => loadTaxonomy({ silent: true }),
          (error) => {
            patchState(store, { loading: false, error });
            loadTaxonomy({});
          },
        ),
      ),
    );

    const deleteNode = rxMethod<TaxonomyNode>((node$) =>
      node$.pipe(
        switchMap((node) => taxonomyHttpService.deleteNode(node)),
        tapAllResponses<Taxonomy[], Error>(
          () => loadTaxonomy({ silent: true }),
          (error) => {
            patchState(store, { loading: false, error });
            loadTaxonomy({});
          },
        ),
      ),
    );

    const editNode = rxMethod<TaxonomyNode>((node$) =>
      node$.pipe(
        switchMap((node) => taxonomyHttpService.editNode(node)),
        tapAllResponses<Taxonomy[], Error>(
          () => loadTaxonomy({ silent: true }),
          (error) => {
            patchState(store, { loading: false, error });
            loadTaxonomy({});
          },
        ),
      ),
    );

    return { loadTaxonomy, addNode, deleteNode, editNode, setSelected, setDisableAllDescendants };
  }),
  withHooks({
    onInit(store) {
      store.loadTaxonomy({});

      const userSignal = toSignal(inject(UserService).currentUser$, { initialValue: null });

      effect(() => {
        const user = userSignal();

        if (!user) {
          return;
        }

        if (store.requestedOrganisationId()) {
          return;
        }

        store.loadTaxonomy({});
      });
    },
  }),
);

const collectAllChildren = (nodes: TaxonomyNode[], acc: TaxonomyNode[]) => {
  for (const node of nodes) {
    acc.push(node);
    if (node.children?.length) {
      collectAllChildren(node.children, acc);
    }
  }
};

const flattenTaxonomy = (
  nodes: TaxonomyNode[] | null | undefined,
  acc: Record<string, TaxonomyNode> = {},
): Record<string, TaxonomyNode> => {
  if (!nodes) {
    return acc;
  }

  for (const node of nodes) {
    if (node.id) {
      acc[node.id] = node;
    }

    if (node.children?.length) {
      flattenTaxonomy(node.children, acc);
    }
  }

  return acc;
};
